import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { MobileHeader } from "./components/MobileHeader";
import { InstallPrompt } from "./components/InstallPrompt";
import { PWAUpdatePrompt } from "./components/PWAUpdatePrompt";
import { SecurityHeaders } from "./components/SecurityHeaders";
import { initializeOneSignal } from "./lib/onesignal";
import AuthForm from "./components/AuthForm";
import NotFound from "./pages/NotFound";

// Lazy load all page components
const Chat = lazy(() => import("./pages/Chat"));
const Receipts = lazy(() => import("./pages/Receipts"));
const Settings = lazy(() => import("./pages/Settings"));
const Install = lazy(() => import("./pages/Install"));
const Summary = lazy(() => import("./pages/Summary"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NewReceiptScanner = lazy(() => import("./pages/NewReceiptScanner"));
const ReceiptDetail = lazy(() => import("./pages/ReceiptDetail"));
const ReceiptEdit = lazy(() => import("./pages/ReceiptEdit"));
const AdminUsageDashboard = lazy(() => import("./pages/AdminUsageDashboard"));
const ConnectFirm = lazy(() => import("./pages/ConnectFirm"));
const PilotTest = lazy(() => import("./pages/PilotTest"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      initializeOneSignal();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Not authenticated → show login/signup
  if (!user) {
    return (
      <BrowserRouter>
        <main>
          <AuthForm />
        </main>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen w-full bg-white overflow-hidden">
        <div className="hidden md:block md:fixed md:inset-y-0 md:left-0 md:z-50">
          <Sidebar />
        </div>
        <div className="flex-1 flex flex-col h-screen md:ml-64 bg-white">
          <MobileHeader />
          <TopBar />
          <main className="flex-1 overflow-y-auto bg-white">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Chat />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/receipts" element={<Receipts />} />
                <Route path="/receipts/new" element={<NewReceiptScanner />} />
                <Route path="/receipt/:id" element={<ReceiptDetail />} />
                <Route path="/receipt/:id/edit" element={<ReceiptEdit />} />
                <Route path="/summary" element={<Summary />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/install" element={<Install />} />
                <Route path="/admin/usage" element={<AdminUsageDashboard />} />
                <Route path="/connect-firm" element={<ConnectFirm />} />
                <Route path="/pilot" element={<PilotTest />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </div>
        <InstallPrompt />
      </div>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SecurityHeaders />
        <PWAUpdatePrompt />
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
